<script setup lang="ts">
import { __SOFTDB_ID__ } from "@/constants";
import { FeatureType, useFeaturesStore } from "@/stores/features";
import { Permission, usePermissionStore } from "@/stores/permission";
import { useScopeStore } from "@/stores/scope";
import { ScopeType } from "@soft-db/monitoring-api-client";

const props = defineProps<{
	permission?: Permission | Permission[];
	feature?: FeatureType;

	scope?: ScopeType | "host";
}>();

const permissionStore = usePermissionStore();
const featureStore = useFeaturesStore();
const scopeStore = useScopeStore();

const hasPermissionAccess = computed(() => {
	if (props.permission == null) {
		return true;
	}

	if (Array.isArray(props.permission)) {
		return props.permission.some((p) => permissionStore.hasPermission(p));
	} else {
		return permissionStore.hasPermission(props.permission);
	}
});

const hasScopeAccess = computed(() => {
	if (props.scope == null) {
		return true;
	}

	if (props.scope === "host") {
		return scopeStore.isHost;
	}

	return scopeStore.isScope(props.scope);
});

const hasFeatureAccess = computed(() => {
	if (props.feature == null) {
		return true;
	}

	return featureStore.hasFeature(props.feature);
});

const hasAccess = logicAnd(hasPermissionAccess, hasScopeAccess, hasFeatureAccess);
</script>

<template>
	<slot v-if="hasAccess" />
	<slot v-else name="fallback" />
</template>
